import type { ReviewRelation, ReviewRow } from './guidedImportReview.js';

export type RelationReviewCategory = 'repeated' | 'suggested' | 'conflict' | 'history';

/** A display group, never an identity group or an instruction to merge records. */
export interface RelationReviewGroup {
  readonly id: string;
  readonly name: string;
  readonly code: string | null;
  readonly rowIds: readonly string[];
  readonly proposalIds: readonly string[];
  readonly pendingProposalIds: readonly string[];
  readonly pendingCount: number;
  /** Includes retained conflict evidence, even after a separate decision. */
  readonly conflict: boolean;
  /** Structural eligibility only; explicit review and server-side canonical validation still apply. */
  readonly canBulkLink: boolean;
  readonly category: RelationReviewCategory;
}

/** Normalize presentation spelling only; keep punctuation, inner spaces and leading zeroes. */
export function normalizeRelationReviewCode(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  if (typeof value === 'number' && !Number.isFinite(value)) return null;
  const code = String(value)
    .normalize('NFKC')
    .trim()
    .replace(/[۰-۹]/g, (digit) => String(digit.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (digit) => String(digit.charCodeAt(0) - 0x0660))
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .toLowerCase();
  return code || null;
}

/** Low confidence is not evidence of difference; only a low reported SAME probability qualifies. */
export function isLikelyDifferentSuggestion(proposal: ReviewRelation): boolean {
  return (
    proposal.decision === 'pending' &&
    proposal.source === 'smart' &&
    typeof proposal.probability === 'number' &&
    Number.isFinite(proposal.probability) &&
    proposal.probability >= 0 &&
    proposal.probability < 0.6
  );
}

/** Read-only candidates for an explicit user choice; no decision is made here. */
export function eligibleLikelyDifferentSuggestions(
  proposals: readonly ReviewRelation[],
): readonly ReviewRelation[] {
  return proposals.filter(isLikelyDifferentSuggestion);
}

interface GroupDraft {
  readonly id: string;
  readonly code: string | null;
  readonly proposals: ReviewRelation[];
}

const compareIds = (left: string, right: string): number =>
  left < right ? -1 : left > right ? 1 : 0;

function rowName(row: ReviewRow | undefined): string | null {
  if (!row || typeof row.values.name !== 'string') return null;
  return row.values.name.trim() || null;
}

/** Only supplied pending/linked edges can connect rows; matching codes never add an edge. */
function connectedRows(rowIds: readonly string[], proposals: readonly ReviewRelation[]): boolean {
  if (rowIds.length === 0) return false;
  const edges = new Map(rowIds.map((id) => [id, new Set<string>()]));
  for (const proposal of proposals) {
    if (proposal.decision === 'separate') continue;
    edges.get(proposal.leftRowId)?.add(proposal.rightRowId);
    edges.get(proposal.rightRowId)?.add(proposal.leftRowId);
  }
  const visited = new Set<string>();
  const queue = [rowIds[0]!];
  for (let index = 0; index < queue.length; index += 1) {
    const id = queue[index]!;
    if (visited.has(id)) continue;
    visited.add(id);
    for (const adjacent of edges.get(id) ?? []) {
      if (!visited.has(adjacent)) queue.push(adjacent);
    }
  }
  return visited.size === rowIds.length;
}

/**
 * Group only edges whose two known endpoint rows have the same nonempty code.
 * Other edges stay individual, including edges touching a repeated-code group.
 * All decisions remain available for history; the caller owns pack/category filters.
 */
export function buildRelationReviewGroups(
  rows: readonly ReviewRow[],
  proposals: readonly ReviewRelation[],
): readonly RelationReviewGroup[] {
  const byRowId = new Map(rows.map((row) => [row.rowId, row]));
  const groups = new Map<string, GroupDraft>();
  for (const proposal of proposals) {
    const left = byRowId.get(proposal.leftRowId);
    const right = byRowId.get(proposal.rightRowId);
    const leftCode = normalizeRelationReviewCode(left?.values.code);
    const rightCode = normalizeRelationReviewCode(right?.values.code);
    const code = leftCode !== null && leftCode === rightCode ? leftCode : null;
    const id =
      code === null
        ? `proposal:${JSON.stringify(proposal.proposalId)}`
        : `code:${JSON.stringify(code)}`;
    let group = groups.get(id);
    if (!group) {
      group = { id, code, proposals: [] };
      groups.set(id, group);
    }
    group.proposals.push(proposal);
  }

  return [...groups.values()].map((group) => {
    const rowIds = [
      ...new Set(group.proposals.flatMap((proposal) => [proposal.leftRowId, proposal.rightRowId])),
    ].sort(compareIds);
    const proposalIds = group.proposals.map((proposal) => proposal.proposalId).sort(compareIds);
    const pendingProposalIds = group.proposals
      .filter((proposal) => proposal.decision === 'pending')
      .map((proposal) => proposal.proposalId)
      .sort(compareIds);
    const names = [...new Set(rowIds.map((id) => rowName(byRowId.get(id))).filter(Boolean))];
    const conflict = group.proposals.some((proposal) => proposal.strongConflict);
    const pendingCount = pendingProposalIds.length;
    const category: RelationReviewCategory =
      pendingCount === 0
        ? 'history'
        : conflict
          ? 'conflict'
          : group.code === null
            ? 'suggested'
            : 'repeated';
    return {
      id: group.id,
      name: names.join(' / ') || group.code || 'ردیف منبع',
      code: group.code,
      rowIds,
      proposalIds,
      pendingProposalIds,
      pendingCount,
      conflict,
      canBulkLink:
        pendingCount > 0 &&
        !conflict &&
        rowIds.every((id) => byRowId.get(id)?.errors.length === 0) &&
        !group.proposals.some((proposal) => proposal.decision === 'separate') &&
        connectedRows(rowIds, group.proposals),
      category,
    };
  });
}
